/**
 * 鼠标设备服务类
 * 统一封装鼠标 HID 协议
 */

import {
  type MouseHIDHandle,
  type BatteryInfo,
  type DeviceInfo,
  type MouseSolutionProvider,
  type MouseDeviceInstance,
  type MouseDeviceClass,
  type HidDevice,
  type MouseServiceType,
} from "@eweadn-qiankun/shared-types";

// 方案配置
interface MouseSolutionConfig {
  name: string;
  modulePath: string;
}

const SOLUTION_CONFIGS: Record<MouseSolutionProvider, MouseSolutionConfig> = {
  jiangmeng: {
    name: "匠盟",
    modulePath: "./jiangmeng/GlobalDeviceManager",
  },
  // 贝盈鼠标：型号与多设备以 VID+PID+UUID 为准（Mouse-Pix，见 ./beiying/basic、useConnectDevice）
  beiying: {
    name: "贝盈",
    modulePath: "./beiying/basic",
  },
  freq: {
    name: "富芮坤",
    modulePath: "./jiangmeng/GlobalDeviceManager",
  },
  zhanli: {
    name: "展立",
    modulePath: "./zhanli/basic",
  },
  yxt: {
    name: "芯永信",
    modulePath: "./yxt/basic",
  },
};

/**
 * 模块级集合：MouseService 经 sdkApi Proxy 暴露时，实例上的 Map/WeakMap
 * 会被再包一层 Proxy，调用 .get/.set 会抛
 * “Method WeakMap.prototype.get called on incompatible receiver”。
 * 单例服务用模块级存储即可避开该问题。
 */
const mouseDeviceInstances = new Map<string, MouseDeviceInstance>();
const mouseTempIdMap = new WeakMap<HidDevice, number>();
let mouseTempIdSeq = 0;

/**
 * 鼠标设备服务 - 支持多设备管理
 */
export class MouseService implements MouseServiceType {
  private handle: MouseHIDHandle | null = null;
  private initPromise: Promise<void> | null = null;
  private initialized = false;
  private currentProvider: MouseSolutionProvider = "jiangmeng";

  private deviceClassModule: MouseDeviceClass | null = null;

  /**
   * 初始化服务（懒加载 HIDHandle）
   * @param provider 可选，指定鼠标方案，默认为 jiangmeng
   */
  async init(provider?: MouseSolutionProvider): Promise<void> {
    const targetProvider = provider || this.currentProvider;

    // 已就绪且无需切换方案
    if (this.initialized && this.currentProvider === targetProvider) {
      if (
        targetProvider === "freq" ||
        targetProvider === "beiying" ||
        targetProvider === "zhanli" ||
        targetProvider === "yxt"
      )
        return;
      if (this.handle) return;
    }

    // 切换方案时的处理：
    // 切到富芮坤 / 贝盈时不 reset，避免清空匠盟 deviceInstances（多方案并存时子应用仍要读匠盟实例）。
    if (this.currentProvider !== targetProvider) {
      if (
        targetProvider === "freq" ||
        targetProvider === "beiying" ||
        targetProvider === "zhanli" ||
        targetProvider === "yxt"
      ) {
        this.currentProvider = targetProvider;
        this.handle = null;
        this.deviceClassModule = null;
        this.initialized = true;
        this.initPromise = null;
        if (typeof window !== "undefined") {
          (window as unknown as { mouseHIDHandle?: unknown }).mouseHIDHandle =
            undefined;
        }
        return;
      }

      if (
        this.currentProvider === "freq" ||
        this.currentProvider === "beiying" ||
        this.currentProvider === "zhanli" ||
        this.currentProvider === "yxt"
      ) {
        // 从富芮坤 / 贝盈切回匠盟：不 reset，保留已连接的匠盟 HID 实例
        this.currentProvider = targetProvider;
        this.initPromise = null;
        this.initialized = false;
      } else {
        await this.reset();
        this.currentProvider = targetProvider;
      }
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.doInit(targetProvider);
    return this.initPromise;
  }

  private async doInit(provider: MouseSolutionProvider): Promise<void> {
    const config = SOLUTION_CONFIGS[provider];

    try {
      let HIDHandleModule: { default: MouseHIDHandle };
      let DeviceClassModule: { default: MouseDeviceClass };

      // 根据方案动态导入对应的 HIDHandle 和 DeviceClass
      switch (provider) {
        case "jiangmeng":
          // 新架构: 导入GlobalDeviceManager
          HIDHandleModule = await import(
            /* webpackChunkName: "mouse-hid-jiangmeng" */
            "./jiangmeng/GlobalDeviceManager"
          );
          // 导入设备实例类
          DeviceClassModule = await import(
            /* webpackChunkName: "mouse-hid-jiangmeng-device" */
            "./jiangmeng/JiangmengHIDDevice"
          );
          break;
        case "beiying":
          // 贝盈：Mouse-Pix（./beiying），型号与多设备以 VID+PID+UUID 区分；不走匠盟栈与 connectDevice(JiangmengHIDDevice)。
          this.currentProvider = "beiying";
          this.handle = null;
          this.deviceClassModule = null;
          this.initialized = true;
          if (typeof window !== "undefined") {
            (window as unknown as { mouseHIDHandle?: unknown }).mouseHIDHandle =
              undefined;
          }
          return;
        case "freq":
          // 富芮坤：走 WebHID 协议层（freq/protocol），不使用匠盟 HID 栈
          this.currentProvider = "freq";
          this.handle = null;
          this.deviceClassModule = null;
          this.initialized = true;
          if (typeof window !== "undefined") {
            (window as unknown as { mouseHIDHandle?: unknown }).mouseHIDHandle =
              undefined;
          }
          return;
        case "zhanli":
          // 展立：走 WebHID 协议层（zhanli/deviceProtocol），不使用匠盟 HID 栈
          this.currentProvider = "zhanli";
          this.handle = null;
          this.deviceClassModule = null;
          this.initialized = true;
          if (typeof window !== "undefined") {
            (window as unknown as { mouseHIDHandle?: unknown }).mouseHIDHandle =
              undefined;
          }
          return;
        default:
          throw new Error(`不支持的鼠标方案: ${provider}`);
      }

      this.handle = HIDHandleModule.default as MouseHIDHandle;
      this.deviceClassModule = DeviceClassModule.default;
      this.initialized = true;

      // 同时挂载到 window，兼容旧代码
      if (typeof window !== "undefined") {
        window.mouseHIDHandle = this.handle;
      }
    } catch (error) {
      console.error(`[MouseService] ${config.name}方案初始化失败:`, error);
      this.initPromise = null;
      throw error;
    }
  }

  /**
   * 重置服务状态（切换方案时使用）
   */
  private async reset(): Promise<void> {
    try {
      await this.closeAllDevices();
      if (this.handle) {
        await this.handle.Device_Close();
      }
    } catch (error) {
      console.warn("[MouseService] reset 过程中释放连接失败:", error);
    } finally {
      mouseDeviceInstances.clear();
      this.deviceClassModule = null;
      mouseTempIdSeq = 0;
      this.handle = null;
      this.initPromise = null;
      this.initialized = false;
    }
  }

  /**
   * 获取 HIDHandle 实例（供 mouseDeviceManager 等内部使用）
   */
  getHandle(): MouseHIDHandle | null {
    return this.handle;
  }

  /**
   * 检查服务是否已初始化
   */
  isInitialized(): boolean {
    return this.initialized && this.handle !== null;
  }

  /**
   * 获取当前方案类型
   */
  getProvider(): MouseSolutionProvider {
    return this.currentProvider;
  }

  /**
   * 获取当前方案名称
   */
  getProviderName(): string {
    return SOLUTION_CONFIGS[this.currentProvider].name;
  }

  /**
   * 连接指定设备
   */
  async connect(
    device: HidDevice,
    filters?: Array<{ vendorId?: number; productId?: number }>
  ): Promise<boolean> {
    await this.init();
    if (!this.handle) {
      console.error("[MouseService] HIDHandle 未初始化");
      return false;
    }

    const defaultFilters = filters || [
      { vendorId: device.vendorId, productId: device.productId },
    ];
    return this.handle.Request_Device(defaultFilters, device);
  }

  /**
   * 获取设备信息 (CID/MID/Type)
   */
  async getDeviceInfo(): Promise<DeviceInfo | null> {
    if (!this.handle) {
      return null;
    }

    try {
      const info = await this.handle.Get_Device_Info();
      if (
        info &&
        typeof info.cid === "number" &&
        typeof info.mid === "number" &&
        typeof info.type === "number"
      ) {
        return {
          cid: info.cid,
          mid: info.mid,
          type: info.type,
        };
      }
      return null;
    } catch (error) {
      console.error("[MouseService] 获取设备信息失败:", error);
      return null;
    }
  }

  /**
   * 获取电量信息
   */
  getBattery(stableId?: string): BatteryInfo | null {
    // 如果提供了 stableId，从多设备实例中查找
    if (stableId) {
      const instance = mouseDeviceInstances.get(stableId);
      if (instance?.deviceInfo?.battery) {
        const { level, charging, voltage } = instance.deviceInfo.battery;
        return { level, charging, voltage };
      }
      return null;
    }

    // 后备：使用 legacy handle
    if (!this.handle?.deviceInfo?.battery) {
      return null;
    }

    const { level, charging, voltage } = this.handle.deviceInfo.battery;
    return { level, charging, voltage };
  }

  /**
   * 设备是否在线
   */
  isOnline(stableId?: string): boolean {
    if (stableId) {
      return mouseDeviceInstances.get(stableId)?.deviceInfo?.online ?? false;
    }
    return this.handle?.deviceInfo?.online ?? false;
  }

  /**
   * 设备是否休眠
   */
  isSleeping(stableId?: string): boolean {
    // 展立等方案把休眠写在 window map；优先按 stableId 读 map，避免 provider 未对齐时读不到
    const id =
      stableId ||
      (typeof localStorage !== "undefined"
        ? localStorage.getItem("mouseCurrentStableId")
        : null);
    if (id && typeof window !== "undefined") {
      const map = (
        window as unknown as Record<string, Record<string, boolean> | undefined>
      ).__MOUSE_SLEEP_MAP__;
      if (map && id in map) {
        return map[id] === true;
      }
    }
    if (this.currentProvider === "zhanli") {
      return false;
    }
    if (stableId) {
      return mouseDeviceInstances.get(stableId)?.deviceInfo?.sleeping ?? false;
    }
    return this.handle?.deviceInfo?.sleeping ?? false;
  }

  /**
   * 设备是否有线连接
   */
  isWired(): boolean {
    return this.handle?.deviceInfo?.isWired ?? false;
  }

  /**
   * 关闭设备连接
   */
  async close(): Promise<void> {
    if (this.handle) {
      await this.handle.Device_Close();
    }
  }

  /**
   * 获取完整的 deviceInfo（供需要完整信息的场景使用）
   */
  getDeviceInfoFull(): MouseHIDHandle["deviceInfo"] | null {
    return this.handle?.deviceInfo ?? null;
  }

  /**
   * 连接设备(多设备模式)
   * @param device HID设备对象
   * @param options 配置选项
   * @returns 设备实例stableId
   */
  async connectDevice(
    device: HidDevice,
    options: {
      stableId?: string;
      visit?: boolean;
      freqLinkHint?: "a" | "c";
    } = {}
  ): Promise<string | null> {
    await this.init();

    if (this.currentProvider === "beiying") {
      // 贝盈走 Mouse-Pix（./beiying），非 JiangmengHIDDevice 多实例栈
      return null;
    }

    if (this.currentProvider === "freq") {
      try {
        const { openFreqSession } = await import("./freq/basic");

        const { freqStableIdFromHid } =
          await import("../../utils/connectDevice/freqDeviceIdentity");
        const canonicalStableBaseId =
          freqStableIdFromHid(device) ??
          `mouse-${device.vendorId}-${device.productId}-unknown`;

        const stableId = options.stableId
          ? options.stableId
          : canonicalStableBaseId;

        await openFreqSession(device, stableId, {
          freqLinkHint: options.freqLinkHint,
        });
        return stableId;
      } catch (e) {
        console.error("[MouseService] 富芮坤 connectDevice 失败:", e);
        return null;
      }
    }

    if (this.currentProvider === "zhanli") {
      try {
        const { openZhanliSession } = await import("./zhanli/basic");
        const rawProductName =
          typeof device.productName === "string" ? device.productName : "";
        const safeProductName = rawProductName.replace(/-/g, "_").trim();
        const canonicalStableBaseId = `mouse-${device.vendorId}-${device.productId}-${safeProductName || "unknown"}`;
        const stableId = options.stableId
          ? options.stableId
          : canonicalStableBaseId;
        await openZhanliSession(device, stableId);
        return stableId;
      } catch (e) {
        console.error("[MouseService] 展立 connectDevice 失败:", e);
        return null;
      }
    }

    if (!this.deviceClassModule) {
      console.error("[MouseService] 设备类未初始化");
      return null;
    }

    try {
      // canonicalStableId: 与历史保持一致 (mouse-vid-pid-serial/unknown)
      const canonicalStableId = this._generateStableId(device);

      // tempStableId: 解决 serialNumber 缺失导致的多设备 stableId 冲突问题
      // 注意：这里只用于内部 Map key，连接成功后会尽量升级为包含 CID/MID/Type 的稳定 key
      const tempStableId = options.stableId
        ? options.stableId
        : device.serialNumber
          ? canonicalStableId
          : `${canonicalStableId}-tmp${this._getTempId(device)}`;

      // 检查是否已连接
      // 遍历所有已连接的实例，检查是否是同一个物理设备对象(HidDevice)
      for (const instance of mouseDeviceInstances.values()) {
        if (instance.device === device) {
          console.warn(
            `[MouseService] 设备已连接(复用实例): ${instance.stableId}`
          );
          return instance.stableId;
        }
      }

      // 创建设备实例
      const deviceInstance = new this.deviceClassModule(device, {
        stableId: tempStableId,
        visit: options.visit || false,
      });

      // 连接设备
      const success = await deviceInstance.connect();
      if (!success) {
        return null;
      }

      // 连接成功，尝试将 stableId 升级为：mouse-vid-pid-serial/unknown-cid-mid-type
      // 这样在 serialNumber 缺失时仍能区分不同型号/连接类型。
      const info = deviceInstance.getDeviceInfo()?.info;
      const cid = info?.cid;
      const mid = info?.mid;
      const type = info?.type;

      const canUpgrade =
        !options.stableId &&
        typeof cid === "number" &&
        typeof mid === "number" &&
        typeof type === "number";

      if (canUpgrade) {
        let upgradedStableId = `${canonicalStableId}-${cid}-${mid}-${type}`;

        // 检查 ID 冲突，如果有冲突且不是同一个设备，则添加后缀
        let suffix = 0;
        const originalId = upgradedStableId;

        // 循环直到找到一个空闲的 ID
        while (mouseDeviceInstances.has(upgradedStableId)) {
          suffix++;
          upgradedStableId = `${originalId}-${suffix}`;
        }

        if (suffix > 0) {
          console.warn(
            `[MouseService] ID冲突，分配新ID: ${upgradedStableId} (原ID: ${originalId})`
          );
        }

        // 先写入升级后的 key（避免中间状态下查不到）
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (deviceInstance as any).stableId = upgradedStableId;
        mouseDeviceInstances.set(upgradedStableId, deviceInstance);

        // 清理旧 key
        mouseDeviceInstances.delete(tempStableId);

        return upgradedStableId;
      }

      // 无法升级时：用 tempStableId 作为 key
      mouseDeviceInstances.set(tempStableId, deviceInstance);
      return tempStableId;
    } catch (error) {
      console.error("[MouseService] connectDevice 失败:", error);
      return null;
    }
  }

  /**
   * 当 HID 设备没有 serialNumber 时，用临时序号区分不同设备。
   */
  private _getTempId(device: HidDevice): number {
    const existing = mouseTempIdMap.get(device);
    if (typeof existing === "number") {
      return existing;
    }
    mouseTempIdSeq += 1;
    mouseTempIdMap.set(device, mouseTempIdSeq);
    return mouseTempIdSeq;
  }

  /**
   * 获取设备实例
   */
  getDeviceInstance(stableId: string): MouseDeviceInstance | null {
    return mouseDeviceInstances.get(stableId) || null;
  }

  /**
   * 断开设备
   */
  async disconnectDevice(stableId: string): Promise<void> {
    const deviceInstance = mouseDeviceInstances.get(stableId);

    if (deviceInstance) {
      await deviceInstance.close();
      mouseDeviceInstances.delete(stableId);
    }
  }

  /**
   * 获取所有已连接设备
   */
  getAllDevices(): string[] {
    return Array.from(mouseDeviceInstances.keys());
  }

  /**
   * 生成设备唯一标识
   */
  private _generateStableId(device: HidDevice): string {
    const { vendorId, productId, serialNumber } = device;
    return `mouse-${vendorId}-${productId}-${serialNumber || "unknown"}`;
  }

  /**
   * 清理所有设备
   */
  async closeAllDevices(): Promise<void> {
    const stableIds = Array.from(mouseDeviceInstances.keys());

    for (const stableId of stableIds) {
      await this.disconnectDevice(stableId);
    }
  }
}

// 扩展 Window 接口
declare global {
  interface Window {
    mouseHIDHandle?: MouseHIDHandle | null;
  }
}
